import React, { useEffect, useState, useCallback } from "react";
import {
  RefreshCw, CheckCircle2,
  AlertCircle, Loader2, ChevronUp, ChevronDown, Eye,
} from "lucide-react";
import { getDomainStats, getPolicyMapReport } from "./services/dashboardService";
import PolicyResultModal from "./components/PolicyResultModal";

// ── Transform /policy-maps/{id}/report → PolicyResultModal result shape ───────
const transformPolicyMapReport = (data) => {
  if (!data) return null;
  const report   = data.report ?? {};
  const privacy  = report.privacy_analysis ?? {};
  const meta     = report.report_metadata  ?? {};
  const categories = Object.entries(privacy).map(([name, d]) => ({
    name,
    score:       d.score            ?? 0,
    maxScore:    10,
    confidence:  d.confidence_score ?? 0,
    verdict:     d.indicator        ?? "Neutral",
    description: d.summary          ?? "",
  }));
  const scores = categories.map((c) => c.score).filter(Boolean);
  const overallScore = scores.length
    ? +(scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1)
    : (data.overall_score ?? 0);
  const confs = categories.map((c) => c.confidence).filter(Boolean);
  const avgConfidence = confs.length
    ? Math.round(confs.reduce((a, b) => a + b, 0) / confs.length)
    : null;
  return {
    // title:              meta.report_title        ?? `${data.software_name} Privacy Review`,
    reviewedAt:         meta.review_date
                          ? new Date(meta.review_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                          : "",
    policyAsOf:         report.policy_date ?? null,
    overallScore,
    categoriesAnalyzed: categories.length,
    avgConfidence,
    toolSummary:        report.website_summary   ?? "",
    executiveSummary:   report.executive_summary ?? "",
    sourceUrl:          data.privacy_url         ?? "",
    categories,
    softwareName:       data.software_name       ?? "",
  };
};

// ── helpers ───────────────────────────────────────────────────────────────────
const fmt = (dt) => {
  if (!dt) return "—";
  const d = new Date(dt);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    + " · " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
};

// ── Badge ─────────────────────────────────────────────────────────────────────
const Badge = ({ updated }) =>
  updated ? (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-success bg-success/10 px-2.5 py-0.5 rounded-full">
      <ChevronUp className="w-3 h-3" /> Updated
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-brand-light bg-surface-border/60 px-2.5 py-0.5 rounded-full">
      <ChevronDown className="w-3 h-3" /> No change
    </span>
  );

// ── Main page ─────────────────────────────────────────────────────────────────
const ChangeReportPage = () => {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  // ── Report modal state ───────────────────────────────────────────────────
  const [modalOpen,        setModalOpen]        = useState(false);
  const [modalResult,      setModalResult]      = useState(null);
  const [modalLoading,     setModalLoading]     = useState(false);
  const [modalError,       setModalError]       = useState("");
  const [modalSoftware,    setModalSoftware]    = useState("");

  const handleViewReport = useCallback(async (report) => {
    setModalResult(null);
    setModalError("");
    setModalSoftware(report.software_name ?? "");
    setModalLoading(true);
    setModalOpen(true);
    try {
      const res = await getPolicyMapReport(report.global_report_id);
      setModalResult(transformPolicyMapReport(res?.data ?? null));
    } catch (err) {
      setModalError(err?.message || "Failed to load report.");
    } finally {
      setModalLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getDomainStats();
      setData(res?.data ?? null);
    } catch (err) {
      setError(err?.message || "Failed to load change report.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const myStats   = data?.my_stats  ?? {};
  const myReports = data?.my_reports ?? [];

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-brand-text">Changed Report</h1>
          <p className="text-sm text-brand-muted mt-1">
            Track and compare changes across policy revisions.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-surface-border bg-white text-sm font-medium text-brand-muted hover:text-primary hover:border-primary transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-2xl border border-danger/20 bg-danger/5 px-5 py-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-danger flex-shrink-0" />
          <p className="text-sm text-danger">{error}</p>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 text-primary animate-spin" />
        </div>
      )}

      {!loading && data && (
        <>
          {/* My Stats alert */}
          {myStats.should_show_new_shared_report && (() => {
            const reanalysed = myReports.filter((r) => r.has_been_reanalysed_by_anyone);
            return (
              <div className="rounded-2xl border border-primary/20 bg-primary/5 px-5 py-4 flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                  <p className="text-sm text-primary font-medium">
                    New shared reports are available. Some of your policies have been re-analysed by other users.
                  </p>
                </div>
                {reanalysed.length > 0 && (
                  <div className="flex flex-wrap gap-2 pl-8">
                    {reanalysed.map((r) => (
                      <button
                        key={r.id}
                        onClick={() => handleViewReport(r)}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary bg-white border border-primary/30 px-3 py-1.5 rounded-full hover:bg-primary/5 transition-colors"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        View Changed Report — {r.software_name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* My Reports */}
          <div className="bg-white border border-surface-border rounded-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-surface-border">
              <h2 className="text-base font-semibold text-brand-text">My Policy Reports</h2>
              <p className="text-xs text-brand-muted mt-0.5">{myStats.my_total_subscriptions ?? 0} tracked policies</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-brand-muted text-xs font-medium border-b border-surface-border bg-gray-50">
                    <th className="text-left px-6 py-3 font-medium">Software</th>
                    <th className="text-left px-4 py-3 font-medium">Privacy URL</th>
                    <th className="text-left px-4 py-3 font-medium">Version</th>
                    <th className="text-left px-4 py-3 font-medium">Last Analysed</th>
                    <th className="text-left px-4 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {myReports.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-10 text-center text-sm text-brand-muted">
                        No reports yet.
                      </td>
                    </tr>
                  ) : (
                    myReports.map((r) => (
                      <tr key={r.id} className="border-b border-surface-border last:border-0 hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-3 font-semibold text-brand-text whitespace-nowrap">{r.software_name}</td>
                        <td className="px-4 py-3">
                          <span className="text-brand-muted text-sm break-all">
                            {r.privacy_url}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-brand-muted tabular-nums">
                          v{r.user_version}
                          {r.mapped_global_version > r.user_version && (
                            <span className="ml-1.5 text-xs text-amber-500 font-semibold">
                              → v{r.mapped_global_version} available
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-brand-muted whitespace-nowrap">{fmt(r.last_analyzed_at)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge updated={r.has_been_reanalysed_by_anyone} />
                            {r.has_been_reanalysed_by_anyone && (
                              <button
                                onClick={() => handleViewReport(r)}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                              >
                                <Eye className="w-3.5 h-3.5" />
                                View Changed Report
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </>
      )}

      {/* ── Report Modal ── */}
      {modalOpen && (
        <PolicyResultModal
          result={modalResult}
          loading={modalLoading}
          error={modalError}
          softwareName={modalSoftware}
          onClose={() => { setModalOpen(false); setModalResult(null); }}
        />
      )}
    </div>
  );
};

export default ChangeReportPage;
